import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './MobileWelcome.css';

const T = {
  en: {
    tagline: 'Your Gateway to the World',
    sub: 'Dubai • UAE • Uzbekistan',
    login: 'Login',
    continue: 'Continue as Guest',
    footer: 'Registan Tourism · Est. 2018',
  },
  ru: {
    tagline: 'Ваши ворота в мир',
    sub: 'Дубай • ОАЭ • Узбекистан',
    login: 'Войти',
    continue: 'Продолжить как гость',
    footer: 'Registan Tourism · С 2018',
  },
  uz: {
    tagline: 'Dunyoga darvozangiz',
    sub: 'Dubay • BAA • Oʻzbekiston',
    login: 'Kirish',
    continue: 'Mehmon sifatida davom etish',
    footer: 'Registan Tourism · 2018-yildan',
  },
};

export default function MobileWelcome() {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const t = T[language] || T.en;
  const [stage, setStage] = useState(0);

  useEffect(() => {
    // Ensure "Add to Home Screen" saves the /m route (fixes older iPhones/Safari
    // that use the linked manifest's start_url instead of the current URL).
    const originalManifest = document.querySelector('link[rel="manifest"]');
    const originalHref = originalManifest?.getAttribute('href');
    if (originalManifest) originalManifest.setAttribute('href', '/manifest-mobile.json');

    // Also set the iOS-specific title so the home screen icon reads "Registan"
    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const origAppleTitle = appleTitle?.getAttribute('content');
    if (!appleTitle) {
      appleTitle = document.createElement('meta');
      appleTitle.setAttribute('name', 'apple-mobile-web-app-title');
      document.head.appendChild(appleTitle);
    }
    appleTitle.setAttribute('content', 'Registan');

    const s1 = setTimeout(() => setStage(1), 400);   // dome rises
    const s2 = setTimeout(() => setStage(2), 1400);  // wordmark reveals
    const s3 = setTimeout(() => setStage(3), 2500);  // shimmer + tagline
    const s4 = setTimeout(() => setStage(4), 3400);  // CTA button
    return () => {
      [s1, s2, s3, s4].forEach(clearTimeout);
      if (originalManifest && originalHref) originalManifest.setAttribute('href', originalHref);
      if (appleTitle && origAppleTitle) appleTitle.setAttribute('content', origAppleTitle);
    };
  }, []);

  return (
    <div className="mw-root" data-testid="mobile-welcome">
      {/* Deep cinematic background */}
      <div className="mw-bg-grad" />
      <div className="mw-bg-noise" />

      {/* Twinkling stars */}
      <div className="mw-stars" aria-hidden>
        {Array.from({ length: 60 }).map((_, i) => (
          <span
            key={i}
            className="mw-star"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 65}%`,
              animationDelay: `${Math.random() * 4}s`,
              animationDuration: `${2 + Math.random() * 3}s`,
              opacity: 0.3 + Math.random() * 0.7,
            }}
          />
        ))}
      </div>

      {/* Gold aurora sweep */}
      <div className="mw-aurora" />

      {/* Dubai skyline silhouette */}
      <svg className="mw-skyline" viewBox="0 0 1200 260" preserveAspectRatio="none" aria-hidden>
        <defs>
          <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#0a0d1f" stopOpacity="0" />
            <stop offset="1" stopColor="#000814" stopOpacity="0.95" />
          </linearGradient>
        </defs>
        <path fill="url(#skyGrad)" d="M0 260 L0 190 L40 190 L60 170 L90 170 L90 150 L120 150 L120 130 L150 130 L165 110 L180 130 L200 130 L200 100 L220 100 L230 80 L240 100 L260 100 L260 150 L300 150 L300 100 L320 60 L340 100 L340 150 L380 150 L400 120 L420 150 L440 130 L460 150 L500 150 L500 90 L520 60 L525 40 L530 60 L540 90 L540 150 L580 150 L600 120 L620 150 L640 140 L660 150 L700 150 L700 80 L720 60 L740 80 L740 140 L780 140 L800 110 L820 140 L860 140 L880 100 L900 140 L940 140 L960 120 L980 140 L1020 140 L1040 110 L1060 140 L1100 140 L1120 120 L1140 140 L1180 140 L1200 130 L1200 260 Z" />
      </svg>

      {/* Registan dome + minarets */}
      <div className={`mw-dome-wrap ${stage >= 1 ? 'mw-in' : ''}`}>
        <svg className="mw-dome" viewBox="0 0 240 180" aria-hidden>
          <defs>
            <linearGradient id="domeGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#f5c76a" />
              <stop offset="0.55" stopColor="#c9962c" />
              <stop offset="1" stopColor="#7a5c1a" />
            </linearGradient>
            <linearGradient id="minaretGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#e6b658" />
              <stop offset="1" stopColor="#5a4415" />
            </linearGradient>
            <filter id="glowDome" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="4" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
          {/* Left minaret */}
          <rect x="18" y="60" width="14" height="110" fill="url(#minaretGrad)" />
          <ellipse cx="25" cy="60" rx="12" ry="10" fill="#f5c76a" />
          <rect x="22" y="30" width="6" height="30" fill="#c9962c" />
          {/* Right minaret */}
          <rect x="208" y="60" width="14" height="110" fill="url(#minaretGrad)" />
          <ellipse cx="215" cy="60" rx="12" ry="10" fill="#f5c76a" />
          <rect x="212" y="30" width="6" height="30" fill="#c9962c" />
          {/* Main dome */}
          <path
            d="M40 170 L40 100 Q40 30 120 20 Q200 30 200 100 L200 170 Z"
            fill="url(#domeGrad)"
            filter="url(#glowDome)"
          />
          {/* Arch */}
          <path d="M95 170 L95 130 Q120 105 145 130 L145 170 Z" fill="#241a08" />
          {/* Dome cap */}
          <path d="M100 45 Q120 5 140 45 L140 60 Q120 40 100 60 Z" fill="#f5c76a" />
          <line x1="120" y1="0" x2="120" y2="20" stroke="#f5c76a" strokeWidth="2" />
          <circle cx="120" cy="4" r="3" fill="#f5c76a" />
        </svg>
      </div>

      {/* Wordmark */}
      <div className={`mw-wordmark ${stage >= 2 ? 'mw-reveal' : ''}`}>
        <div className="mw-brand-line1">REGISTAN</div>
        <div className="mw-brand-line2">TOURISM</div>
        <div className={`mw-shimmer ${stage >= 3 ? 'mw-shimmer-run' : ''}`} />
      </div>

      {/* Tagline */}
      <div className={`mw-tagline ${stage >= 3 ? 'mw-in' : ''}`}>
        <div className="mw-tag-main">{t.tagline}</div>
        <div className="mw-tag-sub">
          <span className="mw-dot" /> {t.sub} <span className="mw-dot" />
        </div>
      </div>

      {/* CTA */}
      <div className={`mw-cta-wrap ${stage >= 4 ? 'mw-in' : ''}`}>
        <button
          className="mw-login-btn"
          onClick={() => navigate('/admin')}
          data-testid="mobile-welcome-login-btn"
        >
          <span className="mw-btn-shine" />
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
            <polyline points="10 17 15 12 10 7" />
            <line x1="15" y1="12" x2="3" y2="12" />
          </svg>
          <span>{t.login}</span>
        </button>

        <button
          className="mw-guest-btn"
          onClick={() => navigate('/')}
          data-testid="mobile-welcome-guest-btn"
        >
          {t.continue}
        </button>
      </div>

      {/* Language + footer */}
      <div className={`mw-bottom ${stage >= 4 ? 'mw-in' : ''}`}>
        <div className="mw-lang">
          {['en', 'ru', 'uz'].map((l) => (
            <button
              key={l}
              className={`mw-lang-btn ${language === l ? 'active' : ''}`}
              onClick={() => setLanguage(l)}
              data-testid={`mobile-welcome-lang-${l}`}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="mw-footer">{t.footer}</div>
      </div>
    </div>
  );
}
